/**
 * Melodía en placas de colores (1° ciclo) — parseo ABC → círculos + solfeo.
 */
import { COLORES_PLACA, parsearTokenColor } from './guiaElementosMusicales';

/** Boomwhackers / metalófono escolar: pitch → color de placa */
export const NOTA_A_COLOR_PLACA = {
  C: 'rojo',
  c: 'rojo',
  D: 'naranja',
  d: 'naranja',
  E: 'amarillo',
  e: 'amarillo',
  F: 'verde',
  f: 'verde',
  G: 'verde',
  g: 'verde',
  A: 'azul',
  a: 'azul',
  B: 'morado',
  b: 'morado'
};

export const NOTA_A_SOLFEO = {
  C: 'Do',
  c: 'Do',
  D: 'Re',
  d: 'Re',
  E: 'Mi',
  e: 'Mi',
  F: 'Fa',
  f: 'Fa',
  G: 'Sol',
  g: 'Sol',
  A: 'La',
  a: 'La',
  B: 'Si',
  b: 'Si'
};

const durationUnitsFromAbcTok = (token) => {
  const t = String(token || '');
  const m = t.match(/^[Cz](?:(\d+)(?:\/(\d+))?|\/(\d+)?|(\/\/))?$/);
  if (!m) return null;
  const intNum = m[1] ? Number(m[1]) : null;
  const intDen = m[2] ? Number(m[2]) : null;
  const onlyDen = m[3] != null && m[3] !== '' ? Number(m[3]) : null;
  if (intNum !== null && Number.isFinite(intNum)) {
    if (intDen !== null && Number.isFinite(intDen) && intDen !== 0) return intNum / intDen;
    return intNum;
  }
  if (t.endsWith('//')) return 0.25;
  if (t.endsWith('/')) return 0.5;
  if (onlyDen !== null && Number.isFinite(onlyDen) && onlyDen !== 0) return 1 / onlyDen;
  return 1;
};

const figuraDesdeUnidades = (units) => {
  const u = Number(units);
  if (!Number.isFinite(u)) return 'negra';
  if (Math.abs(u - 4) < 1e-9) return 'blanca';
  if (Math.abs(u - 2) < 1e-9) return 'negra';
  if (Math.abs(u - 1) < 1e-9) return 'corchea';
  if (Math.abs(u - 0.5) < 1e-9) return 'semicorchea';
  return 'negra';
};

/** Extrae notas del cuerpo ABC agrupadas por compás (|). */
export const abcAMelodiaPlacas = (abc = '') => {
  const raw = String(abc || '');
  const lineas = raw.split('\n');
  const musica = lineas
    .filter((l) => !/^[A-Z]:/.test(l.trim()) && l.trim())
    .join(' ')
    .trim();
  if (!musica) return [];

  const compases = musica
    .replace(/\|\]/g, '|')
    .replace(/^\|/, '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);

  const out = [];
  compases.forEach((compas, compasIdx) => {
    const tokens =
      compas.match(/[_=^]?[A-Ga-gz](?:\d+\/\d+|\d+\/|\/\d+|\d+|\/\/)?/g) || [];
    tokens.forEach((tok, posIdx) => {
      const pitchM = tok.match(/^([_=^]?)([A-Ga-gz])/);
      if (!pitchM) return;
      const pitch = pitchM[2];
      const esSilencio = pitch.toLowerCase() === 'z';
      const durPart = tok.slice(pitchM[0].length);
      const units = durationUnitsFromAbcTok(esSilencio ? `z${durPart}` : `C${durPart}`) ?? 2;
      out.push({
        pitch: esSilencio ? 'z' : pitch,
        solfeo: esSilencio ? '—' : NOTA_A_SOLFEO[pitch] || pitch,
        color: esSilencio ? 'silencio' : NOTA_A_COLOR_PLACA[pitch] || parsearTokenColor(pitch) || 'rojo',
        figura: esSilencio ? 'silencio' : figuraDesdeUnidades(units),
        abcTok: tok,
        compas: compasIdx + 1,
        posicion: posIdx + 1
      });
    });
  });
  return out;
};

export const debeUsarPlacasColores = ({ curso = '', metadatos = {} } = {}) => {
  if (metadatos.modoVisual === 'pentagrama') return false;
  if (metadatos.modoVisual === 'placas_colores') return true;
  const cursoLow = String(curso || '').toLowerCase();
  const esBasicoInicial = /(1\s*(°|o|mo|er)?\s*b|primero|1\s*b[aá]sico)/i.test(cursoLow);
  if (!esBasicoInicial) return false;
  const inst = String(metadatos.instrumento || '').toLowerCase();
  return (
    metadatos.notasMetalofono !== false ||
    /metal[oó]fono|xil[oó]fono|boomwhacker|campana|placa|color/.test(inst) ||
    /boomwhacker|campana|placa|color/.test(cursoLow)
  );
};
